import React, { ChangeEvent, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useFormikContext } from 'formik'
import { add, Duration, intervalToDuration, isBefore, sub } from 'date-fns'
import { ContentArea } from '../../../shared/content'
import {
  Section,
  SectionItemForm,
  SectionList,
  SectionTitle,
} from '../../../shared/section'
import { InputWithLabel } from '../../../shared/form'
import {
  convertDateAndTimeToDate,
  extractDateFromTimestamp,
  extractTimeFromTimestamp,
} from '../../../shared/utils/dateUtils'
import {
  formatDateAndTimeForAPI,
  formatDateForAPI,
} from '../../../shared/utils/apiUtils'
import { Checkbox } from '../../../shared/form/Checkbox'
import { GroupFieldConfig } from '../types'
import { translationsActivity } from '../translations/translationsActivity'
import { ActivityFormFields } from './types'
import { getDurationAsString, getDurationFromString } from './dateFormatters'

const InlineDateTime: React.FC = ({ children }) => {
  return (
    <div className="flex flex-col space-y-5 md:flex-row md:space-x-4 md:space-y-0">
      {children}
    </div>
  )
}

const allFieldsHidden = (fieldConfig: GroupFieldConfig | undefined): boolean =>
  fieldConfig !== undefined &&
  fieldConfig.startDate.dateHidden &&
  fieldConfig.startDate.timeHidden &&
  fieldConfig.endDate.dateHidden &&
  fieldConfig.endDate.timeHidden &&
  fieldConfig.dueDate.dateHidden &&
  fieldConfig.dueDate.timeHidden &&
  fieldConfig.duration.hidden &&
  fieldConfig.billable.hidden

const TimingFormSection: React.FC<{
  fieldsConfig: GroupFieldConfig | undefined
}> = ({ fieldsConfig }) => {
  const { values, setFieldValue } = useFormikContext<ActivityFormFields>()
  const [duration, setDuration] = useState<Duration>(() =>
    intervalToDuration({
      start: convertDateAndTimeToDate(values.startDate, values.startTime),
      end: convertDateAndTimeToDate(values.endDate, values.endTime),
    })
  )

  if (allFieldsHidden(fieldsConfig)) {
    return null
  }

  const onFocusDueDate = () => {
    if (
      (values.dueDate === null || values.dueDate === '') &&
      (values.dueTime === null || values.dueTime === '')
    ) {
      setFieldValue(
        'dueDate',
        extractDateFromTimestamp(formatDateForAPI(new Date()))
      )

      setFieldValue(
        'dueTime',
        extractTimeFromTimestamp(formatDateForAPI(new Date()))
      )
    }
  }

  const onChangeStartDate = (value: string) => {
    if (value) {
      const newEndDate = formatDateForAPI(
        add(convertDateAndTimeToDate(value, values.startTime), duration)
      )
      setFieldValue('endDate', extractDateFromTimestamp(newEndDate))
      setFieldValue('endTime', extractTimeFromTimestamp(newEndDate))

      setFieldValue('startDateChanged', true)
    }
  }

  const onChangeStartTime = (value: string) => {
    if (value) {
      const newEndDate = formatDateForAPI(
        add(convertDateAndTimeToDate(values.startDate, value), duration)
      )
      setFieldValue('endDate', extractDateFromTimestamp(newEndDate))
      setFieldValue('endTime', extractTimeFromTimestamp(newEndDate))

      setFieldValue('startDateChanged', true)
    }
  }

  const onChangeEndDate = (value: string) => {
    if (value) {
      const startDate = convertDateAndTimeToDate(
        values.startDate,
        values.startTime
      )
      const newEndDate = convertDateAndTimeToDate(value, values.endTime)

      if (startDate < newEndDate) {
        setFieldValue('endDateChanged', true)
        setFieldValue('durationChanged', false)

        const newDuration = intervalToDuration({
          start: startDate,
          end: newEndDate,
        })
        setDuration(newDuration)
      }
    }
  }

  const onChangeEndTime = (value: string) => {
    if (value) {
      const startDate = convertDateAndTimeToDate(
        values.startDate,
        values.startTime
      )
      const newEndDate = convertDateAndTimeToDate(values.endDate, value)

      if (startDate < newEndDate) {
        setFieldValue('endDateChanged', true)
        setFieldValue('durationChanged', false)

        const newDuration = intervalToDuration({
          start: startDate,
          end: newEndDate,
        })
        setDuration(newDuration)
      }
    }
  }

  return (
    <ContentArea>
      <Section>
        <SectionTitle>
          <FormattedMessage
            id="activity.form.time.title"
            defaultMessage="Time information"
          />
        </SectionTitle>
        <SectionList>
          <SectionItemForm>
            {(!fieldsConfig?.startDate.dateHidden ||
              !fieldsConfig?.startDate.timeHidden) && (
              <InlineDateTime>
                {!fieldsConfig?.startDate.dateHidden && (
                  <InputWithLabel
                    name="startDate"
                    type="date"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onChangeStartDate(event.target.value)
                    }
                    label={
                      <FormattedMessage
                        id="activity.detail.date.start"
                        defaultMessage="Start date"
                      />
                    }
                    required={fieldsConfig?.startDate.required}
                  />
                )}
                {!fieldsConfig?.startDate.timeHidden && (
                  <InputWithLabel
                    name="startTime"
                    type="time"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onChangeStartTime(event.target.value)
                    }
                    label={
                      <FormattedMessage
                        id="activity.detail.time.start"
                        defaultMessage="Start time"
                      />
                    }
                    required={fieldsConfig?.startDate.required}
                  />
                )}
              </InlineDateTime>
            )}
            {(!fieldsConfig?.endDate.dateHidden ||
              !fieldsConfig?.endDate?.timeHidden) && (
              <InlineDateTime>
                {!fieldsConfig?.endDate.dateHidden && (
                  <InputWithLabel
                    name="endDate"
                    type="date"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onChangeEndDate(event.target.value)
                    }
                    label={
                      <FormattedMessage
                        id="activity.detail.date.end"
                        defaultMessage="End date"
                      />
                    }
                    required={fieldsConfig?.endDate.required}
                  />
                )}
                {!fieldsConfig?.endDate.timeHidden && (
                  <InputWithLabel
                    name="endTime"
                    type="time"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onChangeEndTime(event.target.value)
                    }
                    label={
                      <FormattedMessage
                        id="activity.detail.time.end"
                        defaultMessage="End time"
                      />
                    }
                    required={fieldsConfig?.endDate.required}
                  />
                )}
              </InlineDateTime>
            )}
            {(fieldsConfig?.dueDate.dateHidden ||
              fieldsConfig?.dueDate.timeHidden) && (
              <InlineDateTime>
                {!fieldsConfig?.dueDate.dateHidden && (
                  <InputWithLabel
                    name="dueDate"
                    type="date"
                    label={
                      <FormattedMessage
                        id="activity.detail.date.due"
                        defaultMessage="Due date"
                      />
                    }
                    onFocus={onFocusDueDate}
                    required={fieldsConfig?.dueDate.required}
                  />
                )}
                {!fieldsConfig?.dueDate.timeHidden && (
                  <InputWithLabel
                    name="dueTime"
                    type="time"
                    label={
                      <FormattedMessage
                        id="activity.detail.time.due"
                        defaultMessage="Due time"
                      />
                    }
                    onFocus={onFocusDueDate}
                    required={fieldsConfig?.dueDate.required}
                  />
                )}
              </InlineDateTime>
            )}
            <InlineDateTime>
              {!fieldsConfig?.duration.hidden && (
                <DurationField duration={duration} setDuration={setDuration} />
              )}
              {!fieldsConfig?.billable.hidden && (
                <Checkbox
                  name="billable"
                  label={
                    <FormattedMessage
                      {...translationsActivity.detailBillable}
                    />
                  }
                />
              )}
            </InlineDateTime>
          </SectionItemForm>
        </SectionList>
      </Section>
    </ContentArea>
  )
}

const DurationField: React.FC<{
  duration: Duration
  setDuration: React.Dispatch<React.SetStateAction<Duration>>
}> = ({ duration, setDuration }) => {
  const { values, setFieldValue } = useFormikContext<ActivityFormFields>()

  const onFocus = () => {
    const defaultDuration = getDurationFromString('00:30')

    if (
      values.startDate !== null &&
      values.startDate !== '' &&
      values.startTime !== null &&
      values.startTime !== '' &&
      (values.endDate === null || values.endDate === '') &&
      (values.endTime === null || values.endTime === '') &&
      defaultDuration !== null
    ) {
      const endDate = formatDateForAPI(
        add(
          convertDateAndTimeToDate(values.startDate, values.startTime),
          defaultDuration
        )
      )

      setFieldValue('endDate', extractDateFromTimestamp(endDate))
      setFieldValue('endTime', extractTimeFromTimestamp(endDate))
      setDuration(defaultDuration)
    }
  }

  const onChange = (value: string) => {
    const newDuration = getDurationFromString(value)
    if (newDuration !== null && values.startDate && values.startTime) {
      const endDate = formatDateForAPI(
        add(
          convertDateAndTimeToDate(values.startDate, values.startTime),
          newDuration
        )
      )

      setFieldValue('endDate', extractDateFromTimestamp(endDate))
      setFieldValue('endTime', extractTimeFromTimestamp(endDate))
      setFieldValue('durationChanged', true)
      setFieldValue('endDateChanged', false)
      setDuration(newDuration)
    } else if (newDuration !== null && values.endDate && values.endTime) {
      const startDate = formatDateForAPI(
        sub(
          convertDateAndTimeToDate(values.endDate, values.endTime),
          newDuration
        )
      )

      setFieldValue('startDate', extractDateFromTimestamp(startDate))
      setFieldValue('startTime', extractTimeFromTimestamp(startDate))
      setFieldValue('durationChanged', true)
      setFieldValue('startDateChanged', false)
      setDuration(newDuration)
    }
  }

  return (
    <div className="flex-1 flex-col">
      <div className="flex flex-row items-center border-b border-gray-500">
        <label className="w-full">
          <span className="block text-2xs">
            <FormattedMessage {...translationsActivity.detailDateDuration} />
          </span>
          <input
            type="time"
            onFocus={onFocus}
            onChange={(event) => onChange(event.target.value)}
            value={
              values.startDate &&
              values.startTime &&
              values.endDate &&
              values.endTime &&
              isBefore(
                convertDateAndTimeToDate(values.startDate, values.startTime),
                convertDateAndTimeToDate(values.endDate, values.endTime)
              )
                ? getDurationAsString(
                    formatDateAndTimeForAPI(values.startDate, values.startTime),
                    formatDateAndTimeForAPI(values.endDate, values.endTime)
                  )
                : ''
            }
            className="w-full flex-1 border-none bg-transparent pb-1 pl-0 pt-0.5 text-black placeholder:text-gray-700 focus:outline-none"
          />
        </label>
      </div>
    </div>
  )
}

export { TimingFormSection }
